import React from 'react';
import styles from './Typography.module.scss';

type HeadingLevel = 1 | 2 | 3 | 4;
type TextAlign = 'left' | 'center' | 'right';
type TextSize = 'large' | 'base' | 'small';
type PredefinedColor =
  | 'neutral-700'
  | 'neutral-600'
  | 'neutral-500'
  | 'neutral-400'
  | 'neutral-200'
  | 'neutral-100'
  | 'white'
  | 'brand-dark'
  | 'brand-primary'
  | 'brand-light'
  | 'blue-dark'
  | 'blue'
  | 'gold'
  | 'coral';

interface BaseTypographyProps {
  align?: TextAlign;
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface HeadingProps extends BaseTypographyProps {
  level: HeadingLevel;
}

interface TextProps extends BaseTypographyProps {
  size?: TextSize;
  weight?: 'regular' | 'medium' | 'semibold' | 'bold';
  color?: PredefinedColor | string;
  element?: 'p' | 'span';
}

export const Heading: React.FC<HeadingProps> = ({
  level,
  align = 'left',
  className = '',
  children,
  style,
}) => {
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  const classes = [
    styles.base,
    styles[`h${level}`],
    styles[`align${align.charAt(0).toUpperCase()}${align.slice(1)}`],
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <Tag className={classes} style={style}>
      {children}
    </Tag>
  );
};

export const Text: React.FC<TextProps> = ({
  size = 'base',
  weight = 'regular',
  color = 'neutral-600',
  align = 'left',
  className = '',
  children,
  style,
  element = 'p',
}) => {
  const isPredefinedColor = [
    'neutral-700',
    'neutral-600',
    'neutral-500',
    'neutral-400',
    'neutral-200',
    'neutral-100',
    'white',
    'brand-dark',
    'brand-primary',
    'brand-light',
    'blue-dark',
    'blue',
    'gold',
    'coral',
  ].includes(color as string);

  const classes = [
    styles.base,
    styles[`text${size.charAt(0).toUpperCase()}${size.slice(1)}`],
    styles[`weight${weight.charAt(0).toUpperCase()}${weight.slice(1)}`],
    isPredefinedColor ? styles[color] : '',
    styles[`align${align.charAt(0).toUpperCase()}${align.slice(1)}`],
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const combinedStyle = {
    ...style,
    ...(isPredefinedColor ? {} : { color }),
  };

  const Element = element;

  return (
    <Element className={classes} style={combinedStyle}>
      {children}
    </Element>
  );
};
